import {
  Box,
  VStack,
  Text,
  Button,
  Heading,
  Card,
  CardBody,
} from "@chakra-ui/react"
import { FaArrowRight } from "react-icons/fa"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import {
  selectCharacterAvatar,
  updateCharacterAvatar,
} from "../features/character/characterSlice"
import { CharacterAvatarEditor } from "../features/character/CharacterAvatarEditor"
import {
  EditedAvatarProvider,
  useEditedAvatarActions,
} from "../features/character/useEditedCharacter"
import { SIDEWAYS_MQ, SIDEWAYS_SMALL_MQ } from "../components/mediaQueries"

interface CharacterCreationScreenProps {
  onContinue: () => void
}

/**
 * Second step of the welcome flow: the player builds their character before
 * seeing the "how to play" screen. Sits between DisclaimerScreen and
 * StartScreen in GameSlidePage.
 */
const CharacterCreationScreen: React.FC<CharacterCreationScreenProps> = ({
  onContinue,
}) => (
  <EditedAvatarProvider data={useAppSelector(selectCharacterAvatar)}>
    <CharacterCreationScreenWithContext onContinue={onContinue} />
  </EditedAvatarProvider>
)

const CharacterCreationScreenWithContext: React.FC<
  CharacterCreationScreenProps
> = ({ onContinue }) => {
  const dispatch = useAppDispatch()
  // Actions only, so arrow taps re-render the editor, not the whole screen
  const { getEditedAvatar } = useEditedAvatarActions()

  const handleContinue = () => {
    dispatch(updateCharacterAvatar(getEditedAvatar()))
    onContinue()
  }

  return (
    <Box
      // dvh: see App.tsx; 100vh alone overflows a phone with its toolbar.
      minH="100vh"
      sx={{
        "@supports (height: 100dvh)": { minHeight: "100dvh" },
        // A phone held sideways: the figure is as tall as the screen less
        // the page and card padding, so the seven arrow rows beside it stay
        // clear of each other (41px apart at 340 tall).
        [SIDEWAYS_MQ]: {
          padding: "8px",
          "--figure-height": "calc(100vh - 48px)",
          "@supports (height: 100dvh)": {
            "--figure-height": "calc(100dvh - 48px)",
          },
        },
      }}
      position="relative"
      overflow="hidden"
      bgGradient="linear(to-br, #fdf2f8, #fbcfe8 45%, #ede9fe)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={{ base: 4, md: 8 }}
    >
      {/* Decorative dot pattern + soft glows behind the content */}
      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        opacity={0.5}
        bgImage="radial-gradient(#f9a8d4 1.5px, transparent 1.5px)"
        bgSize="26px 26px"
      />
      <Box
        position="absolute"
        top="-120px"
        left="-100px"
        w="380px"
        h="380px"
        borderRadius="full"
        bg="radial-gradient(circle, var(--chakra-colors-pink-300) 0%, transparent 70%)"
        opacity={0.45}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-140px"
        right="-110px"
        w="420px"
        h="420px"
        borderRadius="full"
        bg="radial-gradient(circle, var(--chakra-colors-purple-300) 0%, transparent 70%)"
        opacity={0.4}
        pointerEvents="none"
      />

      <VStack
        spacing={8}
        maxW="800px"
        w="100%"
        position="relative"
        zIndex={1}
        py={{ base: 2, md: 4 }}
        sx={{ [SIDEWAYS_MQ]: { paddingBlock: 0 } }}
      >
        <Card
          bg="whiteAlpha.900"
          borderRadius="3xl"
          boxShadow="2xl"
          w="100%"
          overflow="hidden"
        >
          <CardBody
            p={{ base: 5, md: 8 }}
            sx={{ [SIDEWAYS_MQ]: { padding: "12px 16px" } }}
          >
            {/* Heading, editor, button in a column; on a phone held sideways
                two columns, the heading and the button to the left of the
                editor, meeting at its middle. */}
            <VStack
              spacing={6}
              w="100%"
              sx={{
                [SIDEWAYS_MQ]: {
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gridTemplateRows: "auto auto",
                  gap: "12px 16px",
                  alignItems: "center",
                },
              }}
            >
              {/* Header Section */}
              <VStack
                spacing={3}
                align="stretch"
                w="100%"
                sx={{
                  [SIDEWAYS_MQ]: {
                    gridArea: "1 / 1",
                    alignSelf: "end",
                    gap: "8px",
                  },
                }}
              >
                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  fontWeight="bold"
                  color="pink.600"
                  textAlign="center"
                  sx={{ [SIDEWAYS_MQ]: { fontSize: "20px" } }}
                >
                  Before you start, create your character!
                </Heading>
                <Text
                  fontSize={{ base: "md", md: "lg" }}
                  color="gray.700"
                  textAlign="center"
                  lineHeight="1.6"
                  sx={{
                    [SIDEWAYS_MQ]: { fontSize: "15px", lineHeight: "1.4" },
                  }}
                >
                  Use the arrows to choose her look. You can change it any time
                  during the game.
                </Text>
              </VStack>

              {/* Avatar Editor. The small phone's card touches the screen's
                  edge, so a margin keeps the arrows out from under the fixed
                  music button in the corner. */}
              <Box
                w="100%"
                sx={{
                  [SIDEWAYS_MQ]: { gridArea: "1 / 2 / span 2 / span 1" },
                  [SIDEWAYS_SMALL_MQ]: { marginRight: "28px" },
                }}
              >
                <CharacterAvatarEditor height="var(--figure-height, min(60vh, 460px))" />
              </Box>

              {/* Continue Button */}
              <Button
                size="lg"
                bgGradient="linear(to-r, #ec4899, #db2777)"
                color="white"
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="bold"
                px={{ base: 8, md: 12 }}
                py={8}
                borderRadius="xl"
                boxShadow="xl"
                _hover={{
                  bgGradient: "linear(to-r, #db2777, #be185d)",
                  transform: "scale(1.05)",
                  boxShadow: "2xl",
                }}
                _active={{
                  transform: "scale(0.95)",
                }}
                transition="all 0.2s"
                onClick={handleContinue}
                rightIcon={<FaArrowRight />}
                w="100%"
                maxW="400px"
                sx={{
                  [SIDEWAYS_MQ]: {
                    gridArea: "2 / 1",
                    alignSelf: "start",
                    justifySelf: "center",
                    height: "50px",
                    paddingBlock: 0,
                    paddingInline: "24px",
                    fontSize: "18px",
                    maxWidth: "320px",
                  },
                }}
              >
                This is Me
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  )
}

export default CharacterCreationScreen
